import styled from 'styled-components';

const ProductImageGalleryStyle = styled.div`
	--imageSize: 6.8vw;
	--scrollbarSize: var(--sp0-5x);
	--distance: var(--sp1x);

	.gallery-container {
		display: flex;
		flex-direction: row;
		gap: var(--sp2x);
		align-items: stretch;
	}

	/* Thumbnail Gallery - Left Side (Vertical) */
	.thumbnail-gallery {
		display: flex;
		flex-direction: column;
		gap: var(--sp1x);
		flex-shrink: 0;
		align-self: stretch;
		/* Prevent layout shifts */
		min-height: 0;
		width: calc(var(--imageSize) + var(--scrollbarSize) + var(--distance));

		/* When more than 4 images, enable scrolling */
		&.scrollable {
			overflow: hidden auto;
			/* Set max-height to enable scrolling - calculate based on 4 visible thumbnails */
			max-height: calc(var(--thumbnail-height, 100px) * 4 + var(--sp1x) * 3);
			scroll-behavior: smooth;
			direction: rtl;

			&::-webkit-scrollbar {
				width: var(--scrollbarSize);
			}

			&::-webkit-scrollbar-track {
				background: var(--white);
				border-radius: 2px;
			}

			&::-webkit-scrollbar-thumb {
				background: rgba(0, 0, 0, 0.2);
				border-radius: 2px;

				@media (pointer: fine) {
					@media (hover: hover) {
						&:hover {
							background: rgba(0, 0, 0, 0.3);
						}
					}
				}
			}
		}

		.thumbnail-item {
			width: var(--imageSize);
			/* Height = main image height / 4 */
			/* Height is set via inline style from JavaScript calculation */
			/* Fallback to aspect-ratio if height not set */
			aspect-ratio: 1;
			border: 2px solid transparent;
			border-radius: 4px;
			overflow: hidden;
			cursor: pointer;
			background: var(--white);
			padding: 0;
			transition: border-color 0.3s ease;
			position: relative;
			flex-shrink: 0;
			/* Prevent layout shifts */
			min-height: 0;

			@media (pointer: fine) {
				@media (hover: hover) {
					&:hover {
						border-color: rgba(0, 0, 0, 0.2);
					}
				}
			}

			&.active {
				border-color: var(--green);
				border-width: 2px;
			}

			.thumbnail-image {
				--proportion: 0;

				width: 100%;
				height: 100%;
				display: flex;

				img {
					position: relative !important;
					object-fit: contain !important;
				}
			}
		}
	}

	/* Main Image - Right Side */
	.main-image-wrapper {
		position: relative;
		overflow: hidden;
		display: block;
		flex: 1;
		width: 100%;
		/* Ensure wrapper has height for thumbnail calculation */
		align-self: stretch;

		.favorite {
			position: absolute;
			top: var(--sp2x);
			right: var(--sp2x);
			font-size: var(--h2);
			cursor: pointer;
			z-index: 2;
		}

		.product-image-figure {
			background-repeat: no-repeat;
			background-size: 150%;
			background-position: center center;
			margin: unset;
			position: relative;
			overflow: hidden;

			&.clickable {
				cursor: pointer;
			}

			&.desktop-version {
				@media (pointer: fine) {
					@media (hover: hover) {
						&:hover {
							.product-image {
								opacity: 0;
								visibility: hidden;
								transition:
									opacity 0.2s ease-out,
									visibility 0s linear 0.2s;
							}
						}
					}
				}
			}

			.product-image {
				padding-top: 100%;
				transition:
					opacity 0.3s ease-out,
					visibility 0.3s ease-out;
				position: relative;
				z-index: 1;
				/* Ensure img fully covers the background with solid background */
				background-color: var(--white);
				width: 100%;
				height: 100%;
				top: 0;
				left: 0;
				/* Prevent any background bleed-through */
				backdrop-filter: none;

				/* Ensure the image container covers everything */
				.imageCont {
					position: absolute;
					top: 0;
					left: 0;
					width: 100%;
					height: 100%;
					background-color: var(--white);
					z-index: 1;
					/* Ensure container fully covers parent */
					inset: 0;
				}

				img {
					position: absolute !important;
					top: 0;
					left: 0;
					width: 100% !important;
					height: 100% !important;
					object-fit: contain !important;
					z-index: 2;
					/* Ensure image fully covers container */
					inset: 0;
				}
			}
		}
	}

	/* Lightbox Modal */
	.lightbox-overlay {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(0, 0, 0, 0.8);
		backdrop-filter: blur(8px);
		z-index: 9999999999999999999999; /* Higher than header (z-index: 15) and all other elements */
		display: flex;
		align-items: center;
		justify-content: center;
		padding: var(--sp4x);
		overflow: hidden;
		animation: fadeIn 0.3s ease-out;
		/* Block all interactions with elements behind - header, page content, everything */
		pointer-events: auto;
		/* Ensure overlay is on top of everything */
		isolation: isolate;
	}

	@keyframes fadeIn {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}

	@keyframes slideUp {
		from {
			opacity: 0;
			transform: translateY(20px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	.lightbox-close {
		position: fixed;
		top: var(--sp3x);
		right: var(--sp3x);
		width: 48px;
		height: 48px;
		border-radius: 50%;
		background: rgba(255, 255, 255, 0.95);
		border: 2px solid rgba(255, 255, 255, 0.3);
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 1000000;
		transition: all 0.3s ease;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
		backdrop-filter: blur(10px);

		@media (pointer: fine) {
			@media (hover: hover) {
				&:hover {
					background: rgba(255, 255, 255, 1);
					transform: scale(1.1);
					box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
				}
			}
		}

		&:active {
			transform: scale(0.95);
		}

		svg,
		i {
			font-size: 20px;
			color: var(--black, #252525);
		}
	}

	.lightbox-content {
		display: flex;
		gap: var(--sp4x);
		width: 100%;
		max-width: 1600px;
		height: 100%;
		/* max-height: 90vh; */
		align-items: center;
		animation: slideUp 0.4s ease-out;
		position: relative;
		z-index: 1;
		/* Ensure content is isolated from page elements */
		isolation: isolate;
	}

	.lightbox-thumbnails {
		display: flex;
		flex-direction: column;
		gap: var(--sp1x);
		flex-shrink: 0;
		max-height: 100%;
		overflow: hidden auto;
		padding-inline-end: var(--sp1x);
		scroll-behavior: smooth;
		/* Scrollbar on the left */
		direction: rtl;

		&::-webkit-scrollbar {
			width: var(--scrollbarSize);
		}

		&::-webkit-scrollbar-track {
			background: rgba(255, 255, 255, 0.1);
			border-radius: 2px;
		}

		&::-webkit-scrollbar-thumb {
			background: rgba(255, 255, 255, 0.4);
			border-radius: 2px;

			@media (pointer: fine) {
				@media (hover: hover) {
					&:hover {
						background: rgba(255, 255, 255, 0.6);
					}
				}
			}
		}

		.lightbox-thumbnail {
			width: 120px;
			height: 120px;
			border: 3px solid transparent;
			border-radius: 8px;
			overflow: hidden;
			cursor: pointer;
			background: rgba(255, 255, 255, 0.1);
			padding: 0;
			transition: all 0.3s ease;
			flex-shrink: 0;
			position: relative;
			direction: ltr;

			&::before {
				content: '';
				position: absolute;
				inset: 0;
				border-radius: 4px;
				background: rgba(255, 255, 255, 0.05);
				opacity: 0;
				transition: opacity 0.3s ease;
			}

			@media (pointer: fine) {
				@media (hover: hover) {
					&:hover {
						border-color: rgba(255, 255, 255, 0.6);

						&::before {
							opacity: 1;
						}
					}
				}
			}

			&.active {
				border-color: var(--green, #17aba6);
				border-width: 3px;
				box-shadow: 0 0 0 2px rgba(23, 171, 166, 0.3);
			}

			.thumbnail-image {
				width: 100%;
				height: 100%;
				display: flex;

				--proportion: 0;

				img {
					position: relative !important;
					object-fit: contain !important;
				}
			}
		}
	}

	.lightbox-main-image {
		flex: 1;
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
		height: 100%;
		min-height: 0;
		overflow: hidden;

		.lightbox-image {
			--proportion: 0;

			display: block;
			position: relative;
			width: 100%;
			height: 100%;
			z-index: 1;

			img {
				object-fit: contain !important;
			}
		}

		.lightbox-nav {
			position: absolute;
			top: 50%;
			transform: translateY(-50%);
			width: 56px;
			height: 56px;
			border-radius: 50%;
			background: rgba(255, 255, 255, 0.95);
			border: 2px solid rgba(255, 255, 255, 0.3);
			cursor: pointer;
			display: flex;
			align-items: center;
			justify-content: center;
			z-index: 3;
			transition: all 0.3s ease;
			box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
			backdrop-filter: blur(10px);

			svg {
				width: 24px;
				height: 24px;
				stroke: var(--black, #252525);
				transition: stroke 0.3s ease;
			}

			@media (pointer: fine) {
				@media (hover: hover) {
					&:hover {
						background: rgba(255, 255, 255, 1);
						transform: translateY(-50%) scale(1.1);
						box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);

						svg {
							stroke: var(--green, #17aba6);
						}
					}
				}
			}

			&:active {
				transform: translateY(-50%) scale(0.95);
			}

			&.lightbox-nav-prev {
				left: var(--sp3x);
			}

			&.lightbox-nav-next {
				right: var(--sp3x);
			}
		}
	}

	/* //! 1024 */
	@media only screen and (max-width: ${(props) => props.theme.mediaQuery.DesktopSizeXSMin}) and (min-width: ${(
			props
		) => props.theme.mediaQuery.TabletSize}) {
		.gallery-container {
			flex-direction: column;
			gap: var(--sp1x);
		}

		.thumbnail-gallery {
			width: 100%;
			flex-direction: row;
			overflow: auto hidden !important;
			align-items: flex-start;

			.thumbnail-item {
				flex-shrink: 0;
			}

			&.scrollable {
				direction: unset !important;
				max-height: unset !important;

				padding-bottom: var(--sp0-5x) !important;

				&::-webkit-scrollbar {
					height: var(--scrollbarSize) !important;
				}
			}
		}
	}

	/* //! 768 */
	@media only screen and (max-width: ${(props) => props.theme.mediaQuery.TabletSizeMin}) and (min-width: ${(props) =>
			props.theme.mediaQuery.TabletSizeS}) {
		--imageSize: 16vw;

		.lightbox-content {
			flex-direction: column-reverse;
		}

		.lightbox-thumbnails {
			flex-direction: row;
			width: 100%;
			overflow: auto hidden !important;
			direction: ltr;
			padding-inline-end: 0 !important;
			padding-bottom: var(--sp0-5x) !important;

			&::-webkit-scrollbar {
				height: var(--scrollbarSize) !important;
			}
		}

		.lightbox-main-image {
			width: 100%;
		}
	}

	/* //! Mobile */
	@media only screen and (max-width: ${(props) => props.theme.mediaQuery.TabletSizeSMin}) {
		.gallery-container {
			flex-direction: column;
			gap: var(--sp1x);
		}

		.thumbnail-gallery {
			width: 100%;
			flex-direction: row;
			overflow: auto hidden !important;
			align-items: flex-start;

			.thumbnail-item {
				flex-shrink: 0;
			}

			&.scrollable {
				direction: unset !important;
				max-height: unset !important;

				padding-bottom: var(--sp0-5x) !important;

				&::-webkit-scrollbar {
					height: var(--scrollbarSize) !important;
				}
			}
		}

		.lightbox-content {
			flex-direction: column-reverse;
		}

		.lightbox-thumbnails {
			flex-direction: row;
			width: 100%;
			overflow: auto hidden !important;
			direction: ltr;
			padding-inline-end: 0 !important;
			padding-bottom: var(--sp0-5x) !important;

			&::-webkit-scrollbar {
				height: var(--scrollbarSize) !important;
			}

			.lightbox-thumbnail {
				width: var(--sp10x);
				height: var(--sp10x);
			}
		}

		.lightbox-main-image {
			width: 100%;

			.lightbox-nav {
				width: var(--sp5x) !important;
				height: var(--sp5x) !important;
			}

			.lightbox-nav-prev {
				left: 0 !important;
			}

			.lightbox-nav-next {
				right: 0 !important;
			}
		}

		.lightbox-overlay {
			padding: var(--sp4x) var(--sp2x) !important;
		}
	}
`;

export default ProductImageGalleryStyle;
